/**
 * Loads the C120 planogram and resolves every slot against the live `Products`
 * collection. Read-only: nothing here writes to Firestore.
 *
 * Names, prices and stock status come from the portal so the tool never shows a
 * stale price, while the shelf/facing layout comes from the static planogram.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAllProducts, Product } from '../services/ProductService';
import {
  PlanogramCatalog,
  PlanogramDisplay,
  PlanogramForm,
  PlanogramPanel,
  PlanogramProduct,
  PlanogramSlot,
  RawPlanogramData,
  RawPlanogramItem,
} from '../@types/planogram';

const DATASET_URL = '/data/garden-seeds-planogram.json';
const PANEL_LETTERS = 'ABCDEFGHIJ';

const FORMS: PlanogramForm[] = ['Vegetable', 'Flower', 'Heritage', 'Livingston'];

/**
 * Planogram codes are OCR-derived and collide across products, so quantities key
 * off the portal ItemCode whenever the slot matched a real SKU.
 */
function orderKey(item: RawPlanogramItem): string {
  return item.ic ? `sku:${item.ic}` : `rack:${item.c}:${item.n}`;
}

function resolveForm(raw?: string): PlanogramForm {
  return FORMS.includes(raw as PlanogramForm) ? (raw as PlanogramForm) : 'Accessory';
}

function toProduct(item: RawPlanogramItem, byItemCode: Map<string, Product>): PlanogramProduct {
  const live = item.ic ? byItemCode.get(item.ic) : undefined;
  return {
    key: orderKey(item),
    rackName: item.n,
    rackCode: item.c,
    itemCode: item.ic ?? null,
    name: live?.ItemNameEN || item.dn || item.n,
    nameFr: live?.ItemNameFR || null,
    form: resolveForm(live?.ItemForm || item.f),
    categoryEn: live?.ItemCategoryEN || null,
    subCategoryEn: live?.ItemSubCategoryEN || null,
    price: typeof live?.Price === 'number' ? live.Price : null,
    outOfStock: live?.status === 'out-of-stock',
    // Keyed off the baked ItemCode, not the live row, so a failed product fetch
    // degrades price and stock without making the whole rack unorderable.
    missing: !item.ic,
  };
}

function buildCatalog(raw: RawPlanogramData, products: Product[]): PlanogramCatalog {
  const byItemCode = new Map(products.map((p) => [p.ItemCode, p]));
  const resolved = raw.items.map((item) => toProduct(item, byItemCode));

  const productsByKey: Record<string, PlanogramProduct> = {};
  resolved.forEach((p) => {
    // Two rack labels can map to the same SKU; the first resolution wins.
    if (!productsByKey[p.key]) productsByKey[p.key] = p;
  });

  const displays: PlanogramDisplay[] = raw.displays.map((d) => ({
    id: d.id,
    title: d.title,
    short: d.short,
    pages: d.pages,
    panels: d.panels.map((panel, panelIndex): PlanogramPanel => {
      const slots: PlanogramSlot[][] = panel.rows.map((row, rowIndex) =>
        row.map((itemIndex, colIndex) => ({
          row: rowIndex,
          col: colIndex,
          product: resolved[itemIndex],
        }))
      );
      return {
        label: panel.label,
        letter: PANEL_LETTERS[panelIndex] ?? String(panelIndex + 1),
        side: panel.side,
        rowCount: slots.length,
        colCount: slots[0]?.length ?? 0,
        slots,
        bottom: panel.bottom.map((i) => resolved[i]),
      };
    }),
  }));

  return { title: raw.title, source: raw.source, note: raw.note, displays, productsByKey };
}

export interface UsePlanogramResult {
  catalog: PlanogramCatalog | null;
  loading: boolean;
  error: string | null;
  /** True when the planogram loaded but the live product join was unavailable. */
  degraded: boolean;
  refetch: () => Promise<void>;
}

export function usePlanogram(): UsePlanogramResult {
  const [raw, setRaw] = useState<RawPlanogramData | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [degraded, setDegraded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDegraded(false);
    try {
      const response = await fetch(DATASET_URL, { cache: 'no-cache' });
      if (!response.ok) throw new Error(`Planogram dataset returned ${response.status}`);
      setRaw((await response.json()) as RawPlanogramData);
    } catch (err) {
      console.error('usePlanogram: failed to load planogram dataset', err);
      setError('Could not load the planogram layout. Pull down to retry.');
      setLoading(false);
      return;
    }

    // A failed product fetch still leaves a usable rack: fall back to rack labels.
    try {
      setProducts(await getAllProducts());
    } catch (err) {
      console.error('usePlanogram: failed to load live products', err);
      setProducts([]);
      setDegraded(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const catalog = useMemo(
    () => (raw ? buildCatalog(raw, products) : null),
    [raw, products]
  );

  return { catalog, loading, error, degraded, refetch: load };
}
